/*
 * Drag to scroll — mouse only.
 *
 * Wraps a horizontally scrolling strip (the PDP's question pills, for now) and
 * lets a mouse grab it and pull. Touch and pen are deliberately left out: both
 * already pan the strip natively, and taking the gesture over would only put
 * an approximation of the browser's own scrolling in front of the real thing.
 * `pointerType` is the whole filter — there is no `touch-action` change here,
 * so nothing about the native gesture is disturbed.
 *
 * The element finds its own scroller rather than being told: the first
 * descendant (or itself) whose `overflow-x` is `auto` or `scroll`. That keeps
 * the markup free of a second name for something CSS has already said.
 */

const DS_THRESHOLD = 6; // px of travel before a press counts as a drag
const DS_CLICK_GRACE = 250; // ms to swallow the click a release fires
const DS_SAMPLE_WINDOW = 100; // ms of pointer history that decides the throw
const DS_THROW = 220; // ms the release velocity is projected over
const DS_GLIDE_MIN = 320; // ms floor and ceiling for the settle animation
const DS_GLIDE_MAX = 720;

class DragScroll extends HTMLElement {
  constructor() {
    super();
    this.pointerId = null;
    this.dragging = false;
    this.overflows = false;
    this.suppressClicksUntil = 0;
    this.samples = [];
    this.glideFrame = null;

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onRelease = this.onRelease.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
    this.sync = this.sync.bind(this);
  }

  connectedCallback() {
    this.scroller = this.findScroller();
    if (!this.scroller) return;

    /*
     * Release is watched on the window, not on the strip. `setPointerCapture`
     * is best-effort — it throws if the pointer is no longer active, and the
     * catch below lets the drag carry on without it — but without capture a
     * pointer released outside the strip never fires `pointerup` on it, and
     * the drag would be left running: `is-dragging` stuck on, and every later
     * click swallowed. The window always sees the release.
     *
     * These two are dropped on disconnect and so are re-added on every
     * connect; the same handler and options twice over is a no-op, so it does
     * not matter that this runs again. The listeners on the strip travel with
     * it and are added once.
     */
    window.addEventListener('pointerup', this.onRelease);
    window.addEventListener('pointercancel', this.onRelease);

    if (!this.listening) {
      this.listening = true;
      this.scroller.addEventListener('pointerdown', this.onPointerDown);
      this.scroller.addEventListener('pointermove', this.onPointerMove);
      // Stop the browser's native link drag from hijacking the gesture.
      this.scroller.addEventListener('dragstart', this.onDragStart);
      // Capture phase: the click has to be caught before it reaches the link.
      this.scroller.addEventListener('click', this.onClick, true);
    }

    /*
     * Whether the strip overflows can change without its box changing size —
     * a brand font arriving after first paint reflows the items — so the
     * items are watched too, and `fonts.ready` covers the case where the
     * reflow lands before the observer exists. Toggling `is-draggable` only
     * changes a cursor, so this cannot feed itself a second pass.
     */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.sync);
    this.observe();
  }

  disconnectedCallback() {
    this.stopGlide();
    this.observer?.disconnect();
    // The two window listeners outlive the element otherwise.
    window.removeEventListener('pointerup', this.onRelease);
    window.removeEventListener('pointercancel', this.onRelease);
  }

  observe() {
    if ('ResizeObserver' in window) {
      this.observer = this.observer || new ResizeObserver(this.sync);
      /*
       * The box and its contents both matter. Whether the strip overflows can
       * change because the strip got narrower *or* because an item got wider,
       * and observing the scroller alone only ever sees the first — its own
       * `scrollWidth` moving underneath it raises nothing. Re-observing an
       * element already observed is a no-op, so reconnecting is free.
       */
      this.observer.observe(this.scroller);
      Array.from(this.scroller.children).forEach((child) => this.observer.observe(child));
    }
    this.sync();
  }

  findScroller() {
    return [this, ...this.querySelectorAll('*')].find((element) => {
      const overflow = getComputedStyle(element).overflowX;
      return overflow === 'auto' || overflow === 'scroll';
    });
  }

  /* Only advertise the gesture while there is something to drag to. */
  sync() {
    this.overflows = this.scroller.scrollWidth > this.scroller.clientWidth + 1;
    this.classList.toggle('is-draggable', this.overflows);
  }

  onPointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0 || !this.overflows) return;
    this.stopGlide();
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startScroll = this.scroller.scrollLeft;
    this.dragging = false;
    this.samples = [{ t: event.timeStamp, x: event.clientX }];
  }

  onPointerMove(event) {
    if (event.pointerId !== this.pointerId) return;
    const delta = event.clientX - this.startX;

    // Take over only past a small threshold, so a plain click on a pill still
    // behaves like a click.
    if (!this.dragging) {
      if (Math.abs(delta) < DS_THRESHOLD) return;
      this.dragging = true;
      this.classList.add('is-dragging');
      // Those first few pixels happen before `user-select: none` applies, so
      // drop anything the browser selected on the way in.
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) selection.removeAllRanges();
      try {
        this.scroller.setPointerCapture(this.pointerId);
      } catch (e) {
        /* capture is best-effort; the drag still tracks without it */
      }
    }

    this.scroller.scrollLeft = this.startScroll - delta;
    this.samples.push({ t: event.timeStamp, x: event.clientX });
    while (this.samples.length > 1 && event.timeStamp - this.samples[0].t > DS_SAMPLE_WINDOW) {
      this.samples.shift();
    }
    event.preventDefault();
  }

  onRelease(event) {
    if (this.pointerId === null || (event && event.pointerId !== this.pointerId)) return;

    if (this.dragging) {
      try {
        this.scroller.releasePointerCapture(this.pointerId);
      } catch (e) {
        /* already released */
      }
      if (this.hasAttribute('glide')) this.throwFrom(event);
      this.classList.remove('is-dragging');
      /*
       * Swallow the click the release fires, or the pill under the cursor
       * navigates at the end of every drag. Time-boxed rather than a `once`
       * listener: a drag that ends without a following click would leave one
       * armed, and it would then eat the next genuine click.
       */
      this.suppressClicksUntil = performance.now() + DS_CLICK_GRACE;
    }

    this.pointerId = null;
    this.dragging = false;
  }

  throwFrom(event) {
    const first = this.samples[0];
    const last = this.samples[this.samples.length - 1];
    const elapsed = last && first ? last.t - first.t : 0;
    const velocity = elapsed > 0 ? (last.x - first.x) / elapsed : 0;
    const max = Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
    const projected = Math.max(0, Math.min(max, this.scroller.scrollLeft - velocity * DS_THROW));
    const target = typeof this.snapTo === 'function' ? this.snapTo(projected, velocity) : projected;
    this.glideTo(target, velocity);
  }

  glideTo(target, velocity) {
    this.stopGlide();
    const from = this.scroller.scrollLeft;
    const distance = target - from;
    if (Math.abs(distance) < 1) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.scroller.scrollLeft = target;
      return;
    }
    const speed = Math.abs(velocity || 0);
    const wanted = speed > 0.05 ? Math.abs(distance) / speed : DS_GLIDE_MAX;
    const duration = Math.max(DS_GLIDE_MIN, Math.min(DS_GLIDE_MAX, wanted));
    const start = performance.now();
    this.classList.add('is-gliding');
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      this.scroller.scrollLeft = from + distance * eased;
      if (t < 1) {
        this.glideFrame = requestAnimationFrame(step);
      } else {
        this.glideFrame = null;
        this.classList.remove('is-gliding');
      }
    };
    this.glideFrame = requestAnimationFrame(step);
  }

  stopGlide() {
    if (this.glideFrame) cancelAnimationFrame(this.glideFrame);
    this.glideFrame = null;
    this.classList.remove('is-gliding');
  }

  onDragStart(event) {
    event.preventDefault();
  }

  onClick(event) {
    if (performance.now() >= this.suppressClicksUntil) return;
    event.preventDefault();
    event.stopPropagation();
  }
}

if (!customElements.get('drag-scroll')) {
  customElements.define('drag-scroll', DragScroll);
}
